import { useEffect, useState } from "react";
import { Link, useNavigate } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { Button, Field, Input, Label, Text } from "@andrum/ui";
import { SignUpSchema } from "@andrum/schemas";
import { signUpWithPassword } from "@/features/auth/api/auth-api";
import { AuthMethods } from "@/features/auth/components/AuthMethods";
import { isAuthEnforced } from "@/lib/supabase/client";
import {
  captureReferralFromUrl,
  claimStashedReferralIfAny,
  readStashedReferralCode,
} from "@/features/referrals/api/referrals-api";

export function SignUpScreen() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [refCode, setRefCode] = useState<string | null>(null);

  useEffect(() => {
    setRefCode(captureReferralFromUrl());
  }, []);

  const handleSubmit = async () => {
    setError(null);
    setInfo(null);

    const parsed = SignUpSchema.safeParse({
      email,
      password,
      displayName: displayName.trim() || undefined,
    });
    if (!parsed.success) {
      setError(parsed.error.issues[0]?.message ?? t("errorGeneric"));
      return;
    }

    if (!isAuthEnforced()) {
      void navigate({ to: "/onboarding", search: { restart: false } });
      return;
    }

    setLoading(true);
    const result = await signUpWithPassword(parsed.data);
    setLoading(false);

    if (!result.ok) {
      setError(result.message);
      return;
    }

    if (result.data.needsEmailConfirmation) {
      setInfo(t("auth.welcomeLead"));
      return;
    }

    await claimStashedReferralIfAny();
    void navigate({ to: "/onboarding", search: { restart: false } });
  };

  return (
    <div className="flex h-dvh max-h-dvh flex-col overflow-x-hidden overflow-y-auto overscroll-y-contain bg-background px-5 pt-[calc(env(safe-area-inset-top)+12px)] pb-[calc(env(safe-area-inset-bottom)+12px)] [-webkit-overflow-scrolling:touch]">
      <div className="flex shrink-0 flex-col items-center gap-1.5 pt-1">
        <img
          src="/app-icon-inner.svg"
          alt=""
          width={52}
          height={52}
          className="h-13 w-13 h-[52px] w-[52px] rounded-[14px] shadow-md"
          aria-hidden
        />
        <Text variant="title" as="h1" className="text-center text-xl">
          {t("auth.signupTitle")}
        </Text>
        <Text
          variant="muted"
          className="line-clamp-2 max-w-sm text-center text-sm leading-snug"
        >
          {t("auth.welcomeLead")}
        </Text>
      </div>

      {refCode || readStashedReferralCode() ? (
        <div className="mt-2 shrink-0 rounded-xl border border-primary/25 bg-primary-soft/40 px-3 py-2">
          <Text variant="caption">{t("invite.signupBanner")}</Text>
          <p className="mt-0.5 text-sm font-medium tracking-wide text-primary">
            {refCode ?? readStashedReferralCode()}
          </p>
        </div>
      ) : null}

      <form
        className="mt-3 flex flex-col gap-2.5"
        onSubmit={(event) => {
          event.preventDefault();
          void handleSubmit();
        }}
      >
        <Field className="gap-1">
          <Label htmlFor="displayName" className="text-xs">
            {t("auth.firstName")}
          </Label>
          <Input
            id="displayName"
            type="text"
            autoComplete="name"
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
            placeholder={t("auth.firstName")}
            className="min-h-10"
          />
        </Field>
        <Field className="gap-1">
          <Label htmlFor="email" className="text-xs">
            {t("auth.email")}
          </Label>
          <Input
            id="email"
            type="email"
            autoComplete="email"
            inputMode="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="du@exempel.se"
            className="min-h-10"
          />
        </Field>
        <Field className="gap-1">
          <Label htmlFor="password" className="text-xs">
            {t("auth.password")}
          </Label>
          <Input
            id="password"
            type="password"
            autoComplete="new-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder={t("auth.passwordMin")}
            className="min-h-10"
          />
        </Field>

        {error ? (
          <p className="text-center text-xs text-danger" role="alert">
            {error}
          </p>
        ) : null}
        {info ? (
          <p className="text-center text-xs text-secondary" role="status">
            {info}
          </p>
        ) : null}

        <Button fullWidth type="submit" loading={loading} className="min-h-10">
          {t("auth.signup")}
        </Button>

        <div>
          <AuthMethods mode="signup" compact />
        </div>
      </form>

      <div className="shrink-0 pt-2 text-center">
        <Link to="/login" className="text-sm font-medium text-primary">
          {t("auth.login")}
        </Link>
      </div>
    </div>
  );
}
